<template>
  <div class="export-container">
    <!-- 面包屑 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/admin/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>商品管理</el-breadcrumb-item>
      <el-breadcrumb-item>订单导出</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 搜索栏 -->
    <el-card class="filter-card">
      <div class="search-wrapper">
        <div class="search-toggle" v-show="isMobile" @click="toggleSearch">
          <i :class="isSearchExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
          <span>{{ isSearchExpanded ? '收起筛选' : '展开筛选' }}</span>
        </div>
        <div class="search-content" :class="{ 'search-content-mobile': isMobile }" v-show="!isMobile || isSearchExpanded">
          <el-row :gutter="20">
            <el-col :span="7">
              <el-input
                placeholder="搜索商品名称"
                v-model="searchQuery.title_cn"
                clearable
                size="medium"
                @clear="handleSearch()"
                @keyup.enter.native="handleSearch()">
                <el-button slot="append" icon="el-icon-search" @click="handleSearch()"></el-button>
              </el-input>
            </el-col>
            <el-col :span="3">
              <el-select v-model="searchQuery.status" size="medium" placeholder="商品状态" @change="handleSearch()">
                <el-option
                  v-for="(item, id) in statusMap"
                  :key="id"
                  :label="item.text"
                  :value="Number(id)">
                </el-option>
              </el-select>
            </el-col>
            <el-col :span="3">
              <el-select v-model="searchQuery.category_id" size="medium" placeholder="商品分类" @change="handleSearch()">
                <el-option v-for="item in categoryList" :key="item.category_id" :label="item.name_cn" :value="item.category_id">
                </el-option>
              </el-select>
            </el-col>
            <el-col :span="3">
              <el-select v-model="searchQuery.need_addr" size="medium" placeholder="产品类型" @change="handleSearch()">
                <el-option :value="true" label="实体产品"></el-option>
                <el-option :value="false" label="虚拟产品"></el-option>
              </el-select>
            </el-col>
            <el-col :span="4">
              <el-button type="default" size="medium" @click="resetSearch()">重置</el-button>
            </el-col>
          </el-row>
        </div>
      </div>
    </el-card>

    <!-- 商品卡片网格 -->
    <div class="shops-grid">
      <el-card
        v-for="shop in shopList"
        :key="shop.prod_id"
        class="shop-card"
        :class="{ 'shop-card-ended': shop.status === 3 }">
        
        <div class="shop-header">
          <span class="shop-title">{{ shop.title_cn }}</span>
          <el-tag :type="getStatusType(shop.status)" size="small">
            {{ getStatusText(shop.status) }}
          </el-tag>
        </div>

        <div class="shop-type">
          <i :class="shop.need_addr ? 'el-icon-shopping-bag-1' : 'el-icon-cloudy'"></i>
          {{ shop.need_addr ? '实体产品' : '虚拟产品' }}
        </div>

        <div class="shop-price">
          <i class="el-icon-price-tag"></i>
          ¥{{ shop.price }}
        </div>

        <div class="stock-info">
          <el-progress
            :percentage="getStockPercentage(shop)"
            :status="getProgressStatus(shop)"
            :format="getProgressFormat">
          </el-progress>
        </div>

        <div class="shop-actions">
          <div class="stock-count">
            <span class="current">{{ shop.sales_count || 0 }}</span>
            <span class="separator">/</span>
            <span class="max">{{ shop.stock || 0 }}</span>
            <span class="label">件</span>
          </div>
          <el-button type="primary" size="small" @click="exportExcel(shop.prod_id)">
            <i class="el-icon-download" v-if="!shop.exporting"></i>
            <i class="el-icon-loading" v-else></i>
            导出订单
          </el-button>
        </div>
      </el-card>
    </div>

    <!-- 分页 -->
    <div class="pagination-container" v-if="total > 0">
      <el-pagination
        @size-change="onSizeChange"
        @current-change="onPageChange"
        :current-page="pageNum"
        :page-sizes="[10, 20, 30, 40]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total">
      </el-pagination>
    </div>
  </div>
</template>

<script>
import filterMixin from '@/utils/filter.js'

export default {
  name: 'ShopExport',
  mixins: [filterMixin],
  data() {
      return {
        isSearchExpanded: false,
        isMobile: false,
        sessionKey: 'ShopExportSearch',
        filterFetchMethod: 'fetchShops',
        shopList: [],
        total: 0,
        pageNum: 1,
        pageSize: 8,
        searchQuery: {
          title_cn: '',
          status: null,
          need_addr: null,
          category_id: null
        },
        statusMap: {
          1: { text: '在售', type: 'success' },
          2: { text: '售罄', type: 'warning' },
          3: { text: '下架', type: 'info' }
        },
        categoryList: []
      }
    },
  created() {
    this.fetchShops()
    this.fetchCategories()
    this.checkIsMobile()
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async fetchCategories() {
      try {
        const res = await this.$http.get('shop-cates', {
          params: {
            page: 1,
            size: 100
          }
        })
        if (!res.success) {
          this.$message.error(res.message)
          return
        }
        this.categoryList = res.data
      } catch (err) {
        this.$message.error(err.response?.data?.message || '获取分类列表失败')
      }
    },
    fetchShops() {
      this.$http({
        method: 'get',
        url: 'shop/search',
        params: {
          ...this.searchQuery,
          page: this.pageNum,
          size: this.pageSize
        }
      }).then(res => {
        if (!res.success) return this.$message.error(res.message)
        this.shopList = res.data.map(shop => ({
          ...shop,
          exporting: false
        }))
        this.total = res.total
      }).catch(err => {
        this.$message.error(err.response?.data?.message || '获取商品列表失败')
      })
    },

    handleSearch() {
      this.pageNum = 1
      this.fetchShops()
    },

    resetSearch() {
      this.searchQuery = { title_cn: '', status: null, need_addr: null, category_id: null }
      this.pageNum = 1
      this.pageSize = 8
      sessionStorage.removeItem(this.sessionKey)
      this.fetchShops()
    },

    getStatusText(status) {
      return this.statusMap[status]?.text || '未知状态'
    },

    getStatusType(status) {
      return this.statusMap[status]?.type || ''
    },

    getStockPercentage(shop) {
      if (!shop.stock) return 0
      return Math.round((shop.sales_count / (shop.stock + shop.sales_count)) * 100) || 0
    },

    getProgressStatus(shop) {
      const percentage = this.getStockPercentage(shop)
      if (percentage >= 100) return 'success'
      if (percentage >= 80) return 'warning'
      return null
    },
    
    getProgressFormat(percentage) {
      return `${percentage}%`
    },

    // 导出订单
    exportExcel(prodId) {
      const shop = this.shopList.find(s => s.prod_id === prodId)
      if (!shop) return

      shop.exporting = true
      this.$http.get(`/shop-orders/export/${prodId}`, {
        responseType: 'blob'
      }).then(res => {
        const blob = new Blob([res], { type: 'application/vnd.ms-excel' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        link.href = url
        link.download = `商城订单_《${shop.title_cn}》_${Date.now().toString().substring(12, 8)}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }).catch(err => {
        this.$message.error(err.response?.data?.message || '导出失败')
      }).finally(() => {
        shop.exporting = false
      })
    },

    onSizeChange(newSize) {
      this.pageSize = newSize
      this.fetchShops()
    },

    onPageChange(newPage) {
      this.pageNum = newPage
      this.fetchShops()
    },

    toggleSearch() {
      this.isSearchExpanded = !this.isSearchExpanded
    },

    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    handleResize() {
      const wasMobile = this.isMobile
      this.isMobile = window.innerWidth <= 768
      if (!this.isMobile && wasMobile) {
        this.isSearchExpanded = false
      }
    }
  }
}
</script>

<style scoped>
.export-container {
  padding: 0px;
}

.filter-card {
  padding: 0px;
}

.search-wrapper {
  margin-bottom: 10px;
}

.search-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #666;
  font-size: 14px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  transition: color 0.3s;
}

.search-toggle:hover {
  color: #409eff;
}

.search-toggle i {
  font-size: 14px;
}

.search-content {
  padding-top: 10px;
}

.shops-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin: 20px 0;
}

.shop-card {
  position: relative;
  overflow: hidden;
}

.shop-card-ended {
  opacity: 0.8;
}

.shop-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.shop-title {
  font-size: 15px;
  font-weight: 500;
  color: #303133;
  flex: 1;
  margin-right: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shop-type,
.shop-price {
  color: #606266;
  margin: 8px 0;
  font-size: 12px;
}

.shop-type i,
.shop-price i {
  margin-right: 5px;
}

.stock-info {
  margin: 15px 0;
}

.shop-actions {
  margin-top: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stock-count {
  display: flex;
  align-items: baseline;
  font-size: 12px;
  color: #909399;
}

.stock-count .current {
  font-size: 16px;
  font-weight: 500;
  color: #409EFF;
}

.stock-count .separator {
  margin: 0 3px;
}

.stock-count .label {
  margin-left: 3px;
}

.pagination-container {
  margin-top: 30px;
  text-align: center;
  border-radius: 4px;
  padding: 1px 10px 15px;
  background-color: #fff;
}

.export-container :deep(.el-progress-bar__inner) {
  transition: all 0.3s ease;
}

.export-container :deep(.el-progress-bar__outer) {
  background-color: #f0f2f5;
}

.export-container :deep(.el-progress__text) {
  font-size: 12px !important;
}

@media (max-width: 768px) {
  .export-container {
    padding: 0 5px;
  }

  .search-content-mobile .el-row {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .search-content-mobile .el-row .el-col {
    width: 100% !important;
    margin-bottom: 5px;
  }

  .search-content-mobile .el-input,
  .search-content-mobile .el-select {
    width: 100%;
  }

  .search-toggle {
    padding: 10px 0;
    font-size: 13px;
  }

  .shops-grid {
    grid-template-columns: 1fr;
    gap: 0px;
  }

  .shop-card {
    padding: 10px;
    border-radius: 10px;
  }

  .shop-header {
    margin-bottom: 10px;
  }

  .shop-title {
    font-size: 13px;
  }

  .shop-type,
  .shop-price {
    font-size: 11px;
    margin: 6px 0;
  }

  .stock-info {
    margin: 10px 0;
  }

  .shop-actions {
    flex-direction: row;
    gap: 10px;
    align-items: center;
    justify-content: space-between;
    margin-top: 20px;
  }

  .stock-count {
    font-size: 11px;
  }

  .stock-count .current {
    font-size: 14px;
  }

  .shop-actions .el-button {
    font-size: 12px;
    padding: 6px 12px;
  }

  .pagination-container {
    margin-top: 15px;
  }

  .pagination-container ::v-deep .el-pagination {
    font-size: 11px;
  }

  .pagination-container ::v-deep .el-pagination__sizes {
    display: none;
  }

  .pagination-container ::v-deep .el-pagination .btn-prev,
  .pagination-container ::v-deep .el-pagination .btn-next {
    width: 30px;
    height: 30px;
    line-height: 30px;
  }

  .pagination-container ::v-deep .el-pagination .el-pager li {
    padding: 0 4px;
    min-width: 28px;
    height: 30px;
    line-height: 30px;
  }
}
</style>
